import React, { useState, useRef, useEffect } from "react";
import { Send, Bot, User, RefreshCw, Sparkles } from "lucide-react";

interface Message {
  id: number;
  sender: "user" | "ai";
  text: string;
  time: string;
}

export default function Chat() {
  const [messages, setMessages] = useState<Message[]>([
    {
      id: 1,
      sender: "ai",
      text: "Welcome to Hickter AI Assistant! How can I help you today with Web Development, Mobile Apps, SaaS software, or digital growth strategies?",
      time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    }
  ]);
  const [input, setInput] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const quickPrompts = [
    "What are Hickter's Web & App services?",
    "Explain your custom pricing packages",
    "How does mobile app development work?",
    "Book a technical consultation"
  ];

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping]);

  const handleSend = (userText: string) => {
    if (!userText.trim()) return;

    const newMsg: Message = {
      id: Date.now(),
      sender: "user",
      text: userText,
      time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    };

    setMessages((prev) => [...prev, newMsg]);
    setInput("");
    setIsTyping(true);

    setTimeout(() => {
      let aiReply = "Thank you for reaching out! Our engineering team specializes in custom Web applications, React/Next.js platforms, mobile apps (iOS & Android), and digital growth marketing. Visit our Pricing page or Contact us for custom quotes.";
      const lower = userText.toLowerCase();
      
      if (lower.includes("app") || lower.includes("mobile")) {
        aiReply = "Hickter engineers native and cross-platform mobile apps for iOS and Android using React Native, Flutter, Kotlin, and Swift with cloud API integration.";
      } else if (lower.includes("price") || lower.includes("cost") || lower.includes("package")) {
        aiReply = "We offer flexible pricing starting from ₹4,999 / $79 monthly or turnkey project tiers. Switch between INR (₹) and USD ($) on our Pricing page!";
      } else if (lower.includes("web") || lower.includes("site") || lower.includes("react")) {
        aiReply = "Our web engineering team builds ultra-fast, responsive websites with React, Next.js, WordPress, and Shopify tailored for conversion and high speed.";
      } else if (lower.includes("book") || lower.includes("consultation") || lower.includes("meeting")) {
        aiReply = "You can schedule an interactive 1-on-1 meeting directly on our Meeting page to discuss project scope, architecture, and deployment timelines.";
      }

      setMessages((prev) => [
        ...prev,
        {
          id: Date.now() + 1,
          sender: "ai",
          text: aiReply,
          time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
        }
      ]);
      setIsTyping(false);
    }, 900);
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-[#030712] text-slate-900 dark:text-slate-100 font-sans py-6 sm:py-12 px-3 sm:px-6 transition-colors duration-300">
      <div className="max-w-3xl mx-auto space-y-4 flex flex-col h-[calc(100vh-6rem)] sm:h-[calc(100vh-8rem)]">
        
        {/* HEADER BAR */}
        <div className="flex items-center justify-between bg-white/90 dark:bg-slate-900/90 backdrop-blur-xl p-4 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-md shrink-0">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-blue-600 dark:bg-blue-500 text-white shadow-md shadow-blue-500/25">
              <Bot size={22} className="text-white" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="font-extrabold text-base sm:text-lg text-slate-950 dark:text-white tracking-tight">Hickter AI Assistant</h2>
                <span className="hidden sm:inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-blue-50 dark:bg-blue-500/20 text-blue-700 dark:text-cyan-400 border border-blue-200 dark:border-blue-500/30">
                  <Sparkles size={11} /> Smart AI
                </span>
              </div>
              <div className="flex items-center gap-2 mt-0.5">
                <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                <span className="text-xs text-slate-600 dark:text-slate-400 font-semibold">Online & Instant Response</span>
              </div>
            </div>
          </div>

          <button
            onClick={() => setMessages([messages[0]])}
            className="p-2.5 rounded-xl bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:text-blue-600 dark:hover:text-cyan-400 transition-colors cursor-pointer"
            title="Reset Conversation"
          >
            <RefreshCw size={16} />
          </button>
        </div>

        {/* CHAT MESSAGES AREA */}
        <div className="flex-1 bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl p-4 sm:p-6 rounded-3xl border border-slate-200 dark:border-slate-800 shadow-xl overflow-y-auto space-y-4">
          {messages.map((msg) => (
            <div
              key={msg.id}
              className={`flex items-start gap-3 ${msg.sender === "user" ? "flex-row-reverse" : "flex-row"}`}
            >
              <div className={`p-2.5 rounded-2xl text-white shrink-0 shadow-sm ${
                msg.sender === "user" 
                  ? "bg-blue-600 dark:bg-blue-500" 
                  : "bg-slate-200 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 text-blue-600 dark:text-cyan-400"
              }`}>
                {msg.sender === "user" ? <User size={16} className="text-white" /> : <Bot size={16} className="text-blue-600 dark:text-cyan-400" />}
              </div>

              <div className={`p-4 rounded-2xl max-w-[85%] sm:max-w-[78%] space-y-1.5 shadow-sm ${
                msg.sender === "user"
                  ? "bg-blue-600 dark:bg-blue-500 !text-white rounded-tr-none"
                  : "bg-slate-100/90 dark:bg-slate-800/90 border border-slate-200/80 dark:border-slate-700 text-slate-900 dark:text-slate-100 rounded-tl-none"
              }`}>
                <p className="text-xs sm:text-sm font-medium leading-relaxed">{msg.text}</p>
                <span className={`text-[10px] block text-right font-semibold ${
                  msg.sender === "user" ? "text-blue-100" : "text-slate-500 dark:text-slate-400"
                }`}>
                  {msg.time}
                </span>
              </div>
            </div>
          ))}

          {isTyping && (
            <div className="flex items-center gap-2 text-xs text-blue-600 dark:text-cyan-400 font-bold pt-2">
              <Bot size={16} className="animate-spin" />
              <span>Hickter AI is thinking...</span>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* QUICK SUGGESTION CHIPS */}
        <div className="flex items-center gap-2 overflow-x-auto pb-1 shrink-0 scrollbar-none">
          {quickPrompts.map((prompt, idx) => (
            <button
              key={idx}
              onClick={() => handleSend(prompt)}
              className="px-3.5 py-2 rounded-xl bg-white dark:bg-slate-800/90 border border-slate-200 dark:border-slate-700 text-xs font-bold text-slate-800 dark:text-slate-200 hover:bg-blue-50 dark:hover:bg-slate-700 hover:text-blue-700 dark:hover:text-cyan-400 shadow-xs transition-all whitespace-nowrap cursor-pointer"
            >
              {prompt}
            </button>
          ))}
        </div>

        {/* INPUT FORM */}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleSend(input);
          }}
          className="relative shrink-0"
        >
          <input
            type="text"
            placeholder="Type your message about websites, apps, or pricing..."
            value={input}
            onChange={(e) => setInput(e.target.value)}
            className="w-full text-xs sm:text-sm bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-500 rounded-2xl py-3.5 pl-4 pr-14 focus:border-blue-600 dark:focus:border-cyan-400 focus:ring-4 focus:ring-blue-500/20 outline-none shadow-md transition-all"
          />
          <button
            type="submit"
            className="absolute right-2 top-1/2 -translate-y-1/2 bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white p-2.5 rounded-xl shadow-md hover:scale-105 active:scale-95 transition-all cursor-pointer"
            aria-label="Send message"
          >
            <Send size={16} className="text-white" />
          </button>
        </form>

      </div>
    </div>
  );
}
